import { Routes, Route, Navigate } from "react-router";
import { AuthProvider } from "~/lib/auth";
import { ProtectedRoute } from "~/components/ProtectedRoute";
import { RootLayout } from "~/components/layout/RootLayout";
import { LoginPage } from "~/features/auth/LoginPage";
import { DashboardPage } from "~/features/dashboard/DashboardPage";
import { DevelopersPage } from "~/features/developers/DevelopersPage";
import { DeveloperDetailPage } from "~/features/developers/DeveloperDetailPage";
import { CodePage } from "~/features/code/CodePage";
import { InsightsPage } from "~/features/insights/InsightsPage";
import { ReportersPage } from "~/features/reporters/ReportersPage";
import { ReporterDetailPage } from "~/features/reporters/ReporterDetailPage";
import { TeamsPage } from "~/features/teams/TeamsPage";
import { TeamDetailPage } from "~/features/teams/TeamDetailPage";
import { CostCapitalizationPage } from "~/features/cost-cap/CostCapitalizationPage";
import { AIUsagePage } from "~/features/ai-usage/AIUsagePage";
import { AIImpactPage } from "~/features/ai-impact/AIImpactPage";
import { ReportsPage } from "~/features/reports/ReportsPage";
import {
  AuthorsPage,
  BreakdownPage,
  OverviewPage,
  PacePage,
  TrendsPage,
} from "~/features/delivery";
import { SettingsPage } from "~/features/settings/SettingsPage";
import { GitHubSettingsPage } from "~/features/settings/GitHubSettingsPage";
import { JiraSettingsPage } from "~/features/settings/JiraSettingsPage";
import { CursorSettingsPage } from "~/features/settings/CursorSettingsPage";
import { SlackSettingsPage } from "~/features/settings/SlackSettingsPage";
import { DeveloperIdentitiesPage } from "~/features/settings/DeveloperIdentitiesPage";
import { ProductMappingPage } from "~/features/settings/ProductMappingPage";
import { PDSchedulePage } from "~/features/pd-schedule/PDSchedulePage";
import { ToolsPage } from "~/features/tools/ToolsPage";
import { ToolDetailPage } from "~/features/tools/ToolDetailPage";
import { ToolFormPage } from "~/features/tools/ToolFormPage";
import { LabelsPage } from "~/features/labels/LabelsPage";

export function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* Public routes */}
        <Route path="login" element={<LoginPage />} />

        {/* Protected routes */}
        <Route
          element={
            <ProtectedRoute>
              <RootLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="developers" element={<DevelopersPage />} />
          <Route
            path="developers/compare"
            element={<Navigate to="/developers" replace />}
          />
          <Route
            path="developers/:developerId"
            element={<DeveloperDetailPage />}
          />
          {/* Redirect old digest route to main developer page */}
          <Route
            path="developers/:developerId/digest"
            element={<Navigate to="../" relative="path" replace />}
          />
          <Route path="code" element={<CodePage />} />
          <Route path="code/dora" element={<CodePage />} />
          <Route path="code/prs" element={<CodePage />} />
          <Route path="code/commits" element={<CodePage />} />
          <Route path="code/review-load" element={<CodePage />} />
          <Route path="code/repos" element={<CodePage />} />
          {/* Retired Delivery tabs — Activity and the AI Insights tab folded
              into Health and the global Insights page */}
          <Route path="code/activity" element={<Navigate to="/code" replace />} />
          <Route path="code/insights" element={<Navigate to="/insights" replace />} />
          <Route
            path="code/collaboration"
            element={<Navigate to="/code/review-load" replace />}
          />
          <Route path="prs" element={<Navigate to="/code/prs" replace />} />
          <Route
            path="commits"
            element={<Navigate to="/code/commits" replace />}
          />
          <Route
            path="collaboration"
            element={<Navigate to="/code/review-load" replace />}
          />
          <Route path="insights" element={<InsightsPage />} />
          {/* Ask Aura replaced by the Insights engine */}
          <Route path="chat" element={<Navigate to="/insights" replace />} />
          {/* Projects section retired; old links land on the dashboard */}
          <Route path="projects" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="projects/:projectId"
            element={<Navigate to="/dashboard" replace />}
          />
          <Route
            path="repo-audit"
            element={<Navigate to="/code/repos" replace />}
          />
          <Route path="teams" element={<TeamsPage />} />
          <Route path="teams/:teamId" element={<TeamDetailPage />} />
          <Route path="reporters" element={<ReportersPage />} />
          <Route
            path="reporters/:reporterId"
            element={<ReporterDetailPage />}
          />
          <Route
            path="jira/sprints"
            element={<Navigate to="/dashboard" replace />}
          />
          <Route
            path="cost-capitalization"
            element={<CostCapitalizationPage />}
          />
          <Route
            path="pd-schedule"
            element={<Navigate to="/settings/board-reporting" replace />}
          />
          <Route path="dora" element={<Navigate to="/code/dora" replace />} />
          <Route path="ai-impact" element={<AIImpactPage />} />
          <Route path="ai-usage" element={<AIUsagePage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="delivery" element={<OverviewPage />} />
          <Route path="delivery/trends" element={<TrendsPage />} />
          <Route path="delivery/pace" element={<PacePage />} />
          <Route path="delivery/breakdown" element={<BreakdownPage />} />
          <Route path="delivery/authors" element={<AuthorsPage />} />
          <Route path="aura" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="pm/roadmap"
            element={<Navigate to="/dashboard" replace />}
          />
          <Route
            path="pm/analytics"
            element={<Navigate to="/dashboard" replace />}
          />
          <Route
            path="planning"
            element={<Navigate to="/dashboard" replace />}
          />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="settings/github" element={<GitHubSettingsPage />} />
          <Route path="settings/products" element={<ProductMappingPage />} />
          <Route path="settings/jira" element={<JiraSettingsPage />} />
          <Route path="settings/cursor" element={<CursorSettingsPage />} />
          <Route path="settings/slack" element={<SlackSettingsPage />} />
          <Route
            path="settings/developers"
            element={<DeveloperIdentitiesPage />}
          />
          <Route path="settings/board-reporting" element={<PDSchedulePage />} />
          <Route path="tools" element={<ToolsPage />} />
          <Route path="tools/new" element={<ToolFormPage />} />
          <Route path="tools/:id" element={<ToolDetailPage />} />
          <Route path="tools/:id/edit" element={<ToolFormPage />} />
          <Route path="labels" element={<LabelsPage />} />
        </Route>
      </Routes>
    </AuthProvider>
  );
}
